// src/context/OnboardingContext.tsx
"use client";

import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  PropsWithChildren,
} from "react";
import { useAuth } from "@/context/AuthContext";

// Constante para la key de cache de onboarding (RNF-02)
const ONBOARDING_CACHE_KEY = "onboarding_status";

interface OnboardingState {
  hasPresets: boolean;
  isLoading: boolean;
  isOnboardingComplete: boolean; // alias de hasPresets para claridad semántica
}

interface OnboardingContextType extends OnboardingState {
  completeOnboarding: () => void; // Llamar después de crear primer preset (RF-06)
  initFromSessionStatus: (hasPresets: boolean) => void; // FEATURE-104: inyección desde layout
}

// Valores por defecto del contexto
const OnboardingContext = createContext<OnboardingContextType>({
  hasPresets: false,
  isLoading: true,
  isOnboardingComplete: false,
  completeOnboarding: () => {},
  initFromSessionStatus: () => {},
});

/**
 * Guarda el estado de onboarding en sessionStorage
 */
function writeCachedStatus(hasPresets: boolean): void {
  try {
    sessionStorage.setItem(
      ONBOARDING_CACHE_KEY,
      JSON.stringify({ hasPresets })
    );
  } catch {
    // Ignorar errores de storage (ej: modo privado)
  }
}

/**
 * Limpia el cache de onboarding en sessionStorage
 */
export function clearOnboardingCache(): void {
  try {
    sessionStorage.removeItem(ONBOARDING_CACHE_KEY);
  } catch {
    // Ignorar errores de storage
  }
}

export function OnboardingProvider({
  children,
}: PropsWithChildren): React.ReactElement {
  const { user } = useAuth();
  const [hasPresets, setHasPresets] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // FEATURE-104: Flag para evitar fetch automático si ya fue inicializado vía session-status (R-02)
  const initializedFromSession = useRef(false);

  /**
   * Marca el onboarding como completo después de crear el primer preset (RF-06)
   * Llamar desde build-preset después de crear un preset exitosamente
   */
  const completeOnboarding = useCallback(() => {
    setHasPresets(true);
    writeCachedStatus(true);
  }, []);

  /**
   * FEATURE-104: Inyecta el estado de onboarding desde session-status sin hacer fetch.
   * Llamado por PrivateLayoutContent después de fetchSessionStatus().
   */
  const initFromSessionStatus = useCallback((value: boolean) => {
    initializedFromSession.current = true;
    setHasPresets(value);
    writeCachedStatus(value);
    setIsLoading(false);
  }, []);

  // FEATURE-104: No auto-fetch al cambiar user — el layout siempre inicializa vía initFromSessionStatus.
  // Solo resetear estado en logout para limpiar datos del usuario anterior.
  useEffect(() => {
    if (!user) {
      initializedFromSession.current = false;
      setHasPresets(false);
      setIsLoading(true);
    }
  }, [user]);

  const value: OnboardingContextType = {
    hasPresets,
    isLoading,
    isOnboardingComplete: hasPresets, // alias semántico
    completeOnboarding,
    initFromSessionStatus,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

/**
 * Hook para consumir el contexto de onboarding
 */
export function useOnboarding(): OnboardingContextType {
  return useContext(OnboardingContext);
}
